import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Paper,
  Modal,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import EditNoteIcon from "@mui/icons-material/EditNote";

const EditPeriod = () => {
  const [formValues, setFormValues] = useState({
    id: "",
    startTime: "", // 24-hour: "13:00"
    endTime: "",
    periodSequence: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [canEdit, setCanEdit] = useState(true);

  // State for Info Modal
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { encodedFormId } = useParams();

  // Parse "07:00AM" or "01:00PM" → "07:00" or "13:00"
  const parse12HourTo24 = (time12) => {
    if (!time12) return "";
    const match = time12.match(/^(\d{2}):(\d{2})(AM|PM)$/i);
    if (!match) return time12.slice(0, 5);
    let [_, hours, minutes, period] = match;
    hours = parseInt(hours, 10);
    if (period.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (period.toUpperCase() === "AM" && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  // Convert "13:00" → "01:00PM"
  const to12HourFormat = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${hours12.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}${period}`;
  };

  useEffect(() => {
    const fetchPeriod = async () => {
      const periodId = location.state?.id;
      if (!periodId) {
        setError("No period selected");
        return;
      }

      try {
        const res = await fetch(
          "https://wsmsv1.aidigitalsec.com/api/TimeTable/GetPeriod",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: sessionStorage.getItem("token"),
            },
          }
        );
        const data = await res.json();
        const period = data.find((p) => p.periodId === periodId);

        if (period) {
          setFormValues({
            id: period.periodId,
            startTime: parse12HourTo24(period.startTime), // "01:00PM" → "13:00"
            endTime: parse12HourTo24(period.endTime),
            periodSequence: period.sequenceNo,
          });
        }
      } catch (err) {
        setError("Failed to load period");
      }
    };

    fetchPeriod();
  }, [location.state]);

  const handleUpdate = async () => {
    setLoading(true);
    setError("");

    const payload = {
      startTime: to12HourFormat(formValues.startTime),
      endTime: to12HourFormat(formValues.endTime),
      sequenceNo: parseInt(formValues.periodSequence),
    };

    try {
      const res = await fetch(
        `https://wsmsv1.aidigitalsec.com/api/TimeTable/UpdatePeriod/${formValues.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) throw new Error("Update failed");
      setShowSuccessModal(true);
    } catch (err) {
      setError("Failed to update period");
    } finally {
      setLoading(false);
    }
  };

  // Info sections data for the Info Modal
  const infoSections = [
    {
      icon: <AccessTimeIcon sx={{ color: "#1976d2", fontSize: 22 }} />,
      title: "Time Fields",
      color: "#e3f2fd",
      borderColor: "#1976d2",
      items: [
        { label: "Start Time", desc: "Edit the time at which the period begins. Displayed in 24-hour format in the input, converted to 12-hour AM/PM format before saving." },
        { label: "End Time", desc: "Edit the time at which the period ends. Same 24-hour to 12-hour conversion applies on save." },
        { label: "Pre-filled", desc: "Both time fields are automatically pre-filled with the existing period values when the page loads." },
      ],
    },
    {
      icon: <FormatListNumberedIcon sx={{ color: "#7b1fa2", fontSize: 22 }} />,
      title: "Period Sequence",
      color: "#f3e5f5",
      borderColor: "#7b1fa2",
      items: [
        { label: "Period Sequence", desc: "The sequence number that determines this period's order in the daily timetable. Pre-filled with the current value." },
        { label: "Editable", desc: "You can change the sequence number to reorder this period within the timetable structure." },
      ],
    },
    {
      icon: <EditNoteIcon sx={{ color: "#2e7d32", fontSize: 22 }} />,
      title: "Actions & Permissions",
      color: "#e8f5e9",
      borderColor: "#2e7d32",
      items: [
        { label: "UPDATE", desc: "Saves the changes to the period. Disabled while the update request is in progress or if the user lacks edit permission." },
        { label: "CANCEL", desc: "Discards all changes and navigates back to the previous page without saving." },
        { label: "Authorization", desc: "If the user does not have edit (uModify) permission, all fields and the UPDATE button are disabled." },
        { label: "Error Handling", desc: "If the update fails or no period is found for the given ID, an error message is shown via a Snackbar notification." },
      ],
    },
  ];

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: "#0B1F40",
          padding: "12px 24px",
          borderRadius: "4px 4px 0 0",
          mb: 1,
          display: "flex",
          alignItems: "center",
       justifyContent: 'flex-start',
gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
          Edit Period
        </Typography>

        {/* Info Button */}
        <Tooltip title="View page information & guide" arrow placement="left">
          <IconButton
            onClick={() => setInfoModalOpen(true)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.75)",
              backgroundColor: "rgba(255,255,255,0.1)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              borderRadius: "50%",
              width: 32,
              height: 32,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#1976d2",
                borderColor: "#64B5F6",
                color: "#ffffff",
                boxShadow: "0 0 0 3px rgba(100,181,246,0.25)",
                transform: "scale(1.1)",
              },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper elevation={3} sx={{ p: 4, pt: 6 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            label="Start Time"
            type="time"
            value={formValues.startTime}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, startTime: e.target.value }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={!canEdit}
          />
          <TextField
            label="End Time"
            type="time"
            value={formValues.endTime}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, endTime: e.target.value }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={!canEdit}
          />
        </Box>

        <TextField
          label="Period Sequence"
          value={formValues.periodSequence}
          onChange={(e) =>
            setFormValues((prev) => ({
              ...prev,
              periodSequence: e.target.value,
            }))
          }
          fullWidth
          sx={{ mb: 3 }}
          disabled={!canEdit}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Tooltip title={!canEdit ? "Not authorized to edit" : ""}>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdate}
                disabled={loading || !canEdit}
                sx={{ width: 110, height: 50, fontWeight: "bold" }}
              >
                {loading ? "UPDATING..." : "UPDATE"}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="error"
            onClick={() => navigate(-1)}
            sx={{ width: 110, height: 50, fontWeight: "bold" }}
          >
            CANCEL
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>

      <Modal open={showSuccessModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Box sx={{ bgcolor: "#0B1F40", p: 2 }}>
            <Typography variant="h6" color="white">
              Period Updated
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography>Period updated successfully.</Typography>
            <Box sx={{ mt: 3, textAlign: "right" }}>
              <Button
                variant="contained"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Info Modal */}
      <Modal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", sm: 580, md: 620 },
            maxHeight: "85vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: "0 24px 64px rgba(11,31,61,0.22)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Info Modal Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #0B1F40 0%, #1565C0 100%)",
              padding: "16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.15)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InfoOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  id="info-modal-title"
                  variant="h6"
                  sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
                >
                  Edit Period — Guide
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                  Complete reference for all fields and actions
                </Typography>
              </Box>
            </Box>
            
          </Box>

          {/* Info Modal Scrollable Body */}
          <Box sx={{ overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Page Overview */}
            <Box
              sx={{
                backgroundColor: "#f0f4ff",
                border: "1px solid #c5d3f0",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#1a237e", lineHeight: 1.7 }}>
                The <strong>Edit Period</strong> page allows you to modify an existing timetable period. All fields are pre-filled with the current values. Update the start time, end time, or sequence number and click UPDATE to save your changes.
              </Typography>
            </Box>

            {/* Sections */}
            {infoSections.map((section, sIdx) => (
              <Box
                key={sIdx}
                sx={{
                  backgroundColor: section.color,
                  border: `1px solid ${section.borderColor}30`,
                  borderLeft: `4px solid ${section.borderColor}`,
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  {section.icon}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1a1a2e", fontSize: "0.95rem" }}>
                    {section.title}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                  {section.items.map((item, iIdx) => (
                    <Box key={iIdx} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <Chip
                        label={item.label}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          height: 22,
                          flexShrink: 0,
                          backgroundColor: `${section.borderColor}18`,
                          color: section.borderColor,
                          border: `1px solid ${section.borderColor}40`,
                          mt: "1px",
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "#424242", lineHeight: 1.5, fontSize: "0.82rem" }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            {/* Tip Box */}
            <Box
              sx={{
                backgroundColor: "#fffde7",
                border: "1px solid #f9a825",
                borderRadius: 2,
                p: 2,
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
              }}
            >
              <Typography sx={{ fontSize: "1.1rem", mt: "1px" }}>💡</Typography>
              <Typography variant="body2" sx={{ color: "#5d4037", lineHeight: 1.65, fontSize: "0.82rem" }}>
                <strong>Pro Tip:</strong> The time inputs use 24-hour format for easy entry (e.g. type <em>13:00</em> for 1:00 PM). The system automatically converts them to 12-hour AM/PM format when saving to the server.
              </Typography>
            </Box>

          </Box>

          {/* Info Modal Footer */}
          <Box
            sx={{
              borderTop: "1px solid #e0e0e0",
              px: 2.5,
              py: 1.5,
              display: "flex",
              justifyContent: "flex-end",
              flexShrink: 0,
              backgroundColor: "#fafafa",
            }}
          >
            <Button
              onClick={() => setInfoModalOpen(false)}
              variant="contained"
              color="primary"
              sx={{ fontWeight: 600, px: 3 }}
            >
              Got It
            </Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default EditPeriod;